if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.form.querySelector("[name=id]").disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart = document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector("cart-drawer")) this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";

        // Receive upsell FormData from a separate upsell form (via CustomEvent)
        this._upsellFDs = []; // array of FormData payloads
        this._upsellTimestamp = null; // timestamp for grouping base + upsell items
        this._upsellPrices = {}; // track upsell prices by variant ID
        this._upsellShipDates = {}; // track ship_date_end by variant ID
        this._upsellPromiseDates = {}; // track promise_date by variant ID
        this._basePrice = 0; // store the base product price

        localStorage.setItem("bundle_ts", Math.floor(Date.now()).toString());

        // Initialize base price tracking
        this._initBasePriceTracking();

        this.addEventListener("upsell:formdata", (e) => {
          const fd = e.detail?.formData;
          const ts = e.detail?.timestamp;
          const price = e.detail?.price;
          const variantId = e.detail?.variantId;
          const shipDateEnd = e.detail?.shipDateEnd;
          const promiseDate = e.detail?.promiseDate;

          if (fd) this._upsellFDs.push(fd);
          if (ts) this._upsellTimestamp = ts;

          // Track upsell price
          if (variantId && price) {
            this._upsellPrices[variantId] = price;
            this._updateTotalPrice();
          }

          // Track upsell ship date and promise date for synchronization
          if (variantId) {
            this._upsellShipDates[variantId] = shipDateEnd || 0;
            this._upsellPromiseDates[variantId] = promiseDate || "";
          }
        });

        // Handle upsell deselection
        this.addEventListener("upsell:deselect", (e) => {
          const {variantId, productHandle} = e.detail || {};

          if (variantId) {
            // Remove by variant ID
            this._upsellFDs = this._upsellFDs.filter((fd) => {
              for (const [k, v] of fd.entries()) {
                if (k.match(/items\[\d+\]\[id\]/) && String(v) === String(variantId)) {
                  return false; // Remove this FormData
                }
              }
              return true; // Keep this FormData
            });

            // Remove upsell price
            if (this._upsellPrices[variantId]) {
              delete this._upsellPrices[variantId];
              this._updateTotalPrice();
            }

            // Remove upsell ship date and promise date tracking
            if (this._upsellShipDates[variantId] !== undefined) {
              delete this._upsellShipDates[variantId];
            }
            if (this._upsellPromiseDates[variantId] !== undefined) {
              delete this._upsellPromiseDates[variantId];
            }
          }

          // If no more upsells, reset timestamp
          if (this._upsellFDs.length === 0) {
            this._upsellTimestamp = null;
          }

          console.log("Upsell deselected, remaining:", this._upsellFDs.length);
        });

        // Check initial variant selection state and disable button if needed
        this._checkInitialVariantState();
      }

      _initBasePriceTracking() {
        // Store the initial base price from the price element
        const priceContainer = document.querySelector(".pdp-addtocart-container [data-price-wrapper]");
        if (priceContainer) {
          const priceEl = priceContainer.querySelector("[data-product-price]");
          if (priceEl) {
            this._basePrice = this._parsePriceFromText(priceEl.textContent);
          }
        }

        // Listen for variant changes to update base price
        document.addEventListener("variant:changed", (e) => {
          if (e.detail && e.detail.price) {
            this._basePrice = e.detail.price;
            this._updateTotalPrice();
          }
        });
      }

      _checkInitialVariantState() {
        // Check if product has variants and none is selected
        const idInput = this.form.querySelector("[name=id]");
        const hasOnlyDefaultVariant = this.form.closest("product-form")?.dataset.hasOnlyDefaultVariant === "true";

        if (!hasOnlyDefaultVariant && (!idInput || !idInput.value || idInput.value === "")) {
          // Disable submit button if no variant selected
          if (this.submitButton) {
            this.submitButton.disabled = true;
            this.submitButton.setAttribute("aria-disabled", "true");
          }
        }
      }

      _parsePriceFromText(priceText) {
        if (!priceText) return 0;
        // Remove currency symbols and parse the number
        const cleaned = priceText.replace(/[^0-9.,]/g, "").replace(",", "");
        return parseFloat(cleaned) * 100 || 0; // Return price in cents
      }

      _updateTotalPrice() {
        const priceContainer = document.querySelector(".pdp-addtocart-container [data-price-wrapper]");
        if (!priceContainer) return;

        const priceEl = priceContainer.querySelector("[data-product-price]");
        if (!priceEl) return;

        // Calculate total upsell price
        let totalUpsellPrice = 0;
        for (const variantId in this._upsellPrices) {
          totalUpsellPrice += this._upsellPrices[variantId];
        }

        // Get current base price from the element (in case it was updated by variant change)
        if (!this._basePrice) {
          this._basePrice = this._parsePriceFromText(priceEl.textContent);
        }

        const totalPrice = this._basePrice + totalUpsellPrice;

        // Format and update price display
        const formattedPrice = this._formatMoney(totalPrice);
        priceEl.textContent = formattedPrice;

        // Store original base price as data attribute if not set
        if (!priceEl.dataset.basePrice) {
          priceEl.dataset.basePrice = this._basePrice;
        }
      }

      _formatMoney(cents) {
        const moneyFormat = window.theme?.moneyFormat || "${{amount}}";
        const amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        return moneyFormat.replace("{{amount}}", amount).replace("{{amount_no_decimals}}", Math.round(cents / 100));
      }

      // Method to update base price when variant changes (called from sections.product.js)
      updateBasePrice(price) {
        this._basePrice = price;
        const priceEl = document.querySelector(".pdp-addtocart-container [data-price-wrapper] [data-product-price]");
        if (priceEl) {
          priceEl.dataset.basePrice = price;
        }
        this._updateTotalPrice();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const base = new FormData(this.form);
        const payload = new FormData();

        // --- base item -> items[0][...]
        const baseId = base.get("id");
        const baseQty = Number(base.get("quantity") || 1);
        if (!baseId) {
          console.error("[product-form] Missing base variant id");
          return;
        }

        payload.append("items[0][id]", baseId);
        payload.append("items[0][quantity]", baseQty);

        // selling_plan (if present)
        const sp = base.get("selling_plan");
        if (sp) payload.append("items[0][selling_plan]", sp);

        // properties[...] on the base item
        for (const [k, v] of base.entries()) {
          const m = k.match(/^properties\[(.+?)\]$/);
          if (m && v !== undefined && v !== null && String(v).trim() !== "") {
            payload.append(`items[0][properties][${m[1]}]`, v);
          }
        }

        // B2B safety net: ensure _Order Type is set even if the form input was missing
        if (window.theme && window.theme.isB2B && !payload.has("items[0][properties][_Order Type]")) {
          payload.append("items[0][properties][_Order Type]", "B2B");
        }

        // Add _type = base and _ts properties if there are upsell items
        if (this._upsellFDs && this._upsellFDs.length > 0 && this._upsellTimestamp) {
          payload.append("items[0][properties][_type]", "base");
          payload.append("items[0][properties][_ts]", this._upsellTimestamp);
        }

        // optional: cart attributes/note (top-level)
        for (const [k, v] of base.entries()) {
          if (k === "note" || k.startsWith("attributes[")) {
            payload.append(k, v);
          }
        }

        // --- append any queued upsell items starting at index 1
        let nextIndex = 1;
        nextIndex = this._appendUpsellsInto(payload, nextIndex);

        // sections for cart drawer render
        if (this.cart) {
          payload.append(
            "sections",
            this.cart.getSectionsToRender().map((s) => s.id),
          );
          payload.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = payload;

        fetch(`${routes.cart_add_url}`, config)
          .then((r) => r.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: baseId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButton.querySelector("span").classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {source: "product-form", productVariantId: baseId, cartData: response});
            }
            this.error = false;
            this.cart.renderContents(response);
            if (typeof CrossSellCardImageSync !== "undefined") {
              CrossSellCardImageSync.syncCartDrawer();
            }
          })
          .catch((e) => console.error(e))
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty")) this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            this._upsellFDs = []; // reset after submit
            this._upsellTimestamp = null; // reset timestamp after submit
            this._upsellPrices = {}; // reset upsell prices after submit

            // Reset price display to base price
            this._updateTotalPrice();

            localStorage.setItem("bundle_ts", Math.floor(Date.now()).toString());
            document.querySelectorAll(".pdp-upsell__actions.selected").forEach((el) => {
              el.classList.remove("selected");
              delete el.dataset.selectedVariantId;
              delete el.dataset.selectedPrice;
            });
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector(".product-form__error-message");

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      // Append all queued upsell payloads into main formData starting at nextIndex
      _appendUpsellsInto(formData, nextIndex) {
        if (!this._upsellFDs || this._upsellFDs.length === 0) return nextIndex;

        for (const fd of this._upsellFDs) {
          // Find which source indices exist in this FD (supports items[0], items[1], ...)
          const srcIdxs = new Set();
          for (const [k] of fd.entries()) {
            const m = k.match(/^items\[(\d+)\]\[/);
            if (m) srcIdxs.add(Number(m[1]));
          }
          if (srcIdxs.size === 0) continue;

          // Append each source index as its own new item
          const ordered = Array.from(srcIdxs).sort((a, b) => a - b);
          for (const srcIdx of ordered) {
            // require an id
            let hasId = false;
            for (const [k, v] of fd.entries()) {
              if (k === `items[${srcIdx}][id]` && v) {
                hasId = true;
                break;
              }
            }
            if (!hasId) continue;

            for (const [k, v] of fd.entries()) {
              if (!k.startsWith(`items[${srcIdx}]`)) continue;
              const newKey = k.replace(new RegExp(`^items\\[${srcIdx}\\]`), `items[${nextIndex}]`);
              formData.append(newKey, v);
            }
            nextIndex++;
          }
        }

        return nextIndex;
      }
    },
  );
}

class VariantRadios extends HTMLElement {
  constructor() {
    super();
    this.form = document.querySelector(".pdp-product-form");
    this.addEventListener("change", this.onVariantChange.bind(this));
  }

  onVariantChange(evt) {
    this.updateOptions();
    this.updateMasterId();
    this.updateMedia(evt);
    this.updatePrint(evt);
  }

  updatePrint(data) {
    var options = data.dataset.options;
    var optionContainers = document.querySelectorAll(".option-container");
    var href = window.location.href;

    document.querySelectorAll(".print-url").forEach((el) => {
      el.innerHTML = '<a href="' + href + '">' + href + "</a>";
    });

    if (data.dataset.variant && data.dataset.variant.sku) {
      document.querySelectorAll(".print-sku").forEach((el) => {
        el.textContent = data.dataset.variant.sku;
      });
    }

    document.querySelectorAll(".product-options").forEach((el) => {
      el.textContent = "Product Options Selected:";
    });

    var markup = "";
    Object.values(options || {}).forEach((el) => {
      if (!el || !el.value) return;
      markup +=
        '<div><div class="label"><strong>' +
        el.name +
        "</strong></div><ul><li><span>" +
        el.value +
        "</span></li></ul></div>";
    });

    // The original replaced on the first option and appended for the rest, which
    // amounts to rewriting the container with the full list.
    optionContainers.forEach((el) => {
      el.innerHTML = markup;
    });
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option;
        })
        .includes(false);
    });
  }

  updateMedia(evt) {
    let variantImage = "";
    let mediaId = "";
    const mainSlider = document.querySelector(".hc-main-slider");

    if (!this.currentVariant) {
      let nextSibling = evt.target.nextElementSibling;
      if (nextSibling) {
        variantImage = nextSibling.getAttribute("data-image") || ""; // Fallback to provided thumbnail image
        mediaId = nextSibling.getAttribute("data-media") || "";
      }
    } else {
      if (this.currentVariant.featured_media) {
        // Safely extract the source, falling back across the different possible Shopify keys
        variantImage =
          this.currentVariant.featured_media?.preview_image?.src ||
          this.currentVariant.featured_image?.src ||
          this.currentVariant.featured_media?.src ||
          "";
        mediaId = this.currentVariant.featured_media?.id || "";
      }
    }

    if (variantImage) {
      const getSizedImageUrl = (src, size) => {
        if (!src) return src;

        // Remove existing sizes
        let cleanSrc = src.replace(
          /_(?:[0-9]+x[0-9]*|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|pico|icon)(\.[a-zA-Z0-9]+)/i,
          "$1",
        );
        let urlParts = cleanSrc.split("?");
        let basePath = urlParts[0];
        let queryPart = urlParts[1] ? "?" + urlParts[1] : "";

        // Clean up explicit widths/heights so our resize formatting works
        queryPart = queryPart.replace(/[?&](width|height)=[^&]*/g, "");
        if (queryPart.startsWith("&")) queryPart = "?" + queryPart.slice(1);

        let extMatch = basePath.match(/\.[a-zA-Z0-9]+$/);
        let ext = extMatch ? extMatch[0] : "";
        let baseWithoutExt = ext ? basePath.slice(0, -ext.length) : basePath;

        let newUrl = `${baseWithoutExt}_${size}${ext}`;

        // Enforce webp format explicitly across all files via query parameter
        if (!queryPart.includes("format=webp")) {
          queryPart += (queryPart ? "&" : "?") + "format=webp";
        }

        return newUrl + queryPart;
      };

      const imageSrc = getSizedImageUrl(variantImage, "580x");
      const src480 = getSizedImageUrl(variantImage, "480x");
      const src768 = getSizedImageUrl(variantImage, "768x");
      const src800 = getSizedImageUrl(variantImage, "800x");
      const src1000 = getSizedImageUrl(variantImage, "1000x");
      const src1024 = getSizedImageUrl(variantImage, "1024x");

      const altText =
        this.currentVariant && this.currentVariant.featured_media ? this.currentVariant.featured_media.alt || "" : "";

      const template = `
        <div class="product-single__media" data-media="${mediaId}" data-image="${imageSrc}">
          <picture>
            <source srcset="${src1024}" type="image/webp">
            <img 
              alt="${altText}"
              src="${imageSrc}"
              srcset="
                ${src480} 480w,
                ${src768} 768w,
                ${src800} 1024w,
                ${src1000} 1280w"
              sizes="(max-width: 768px) 100vw, 50vw"
              width="580"
              height="580"
            >
          </picture>
        </div>
      `;
      mainSlider.querySelector(".hc-main-wrap:first-child").innerHTML = template;
      // Update the main image in the print sepcs section
      document.querySelectorAll(".print-main-img img").forEach((img) => {
        img.setAttribute("src", imageSrc);
      });
    }
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll("fieldset"));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll("input")).find((radio) => radio.checked).value;
    });
  }
}

customElements.define("variant-radios", VariantRadios);
